import {useAtom} from 'jotai';
import {useEffect, useMemo, useState} from 'react';
import persistentAtom from 'shared/atoms/persistent-atom';
import {KeyValueProps} from 'shared/types/key-value';
import {NestedKeys} from 'shared/types/key-manipulation';
import {FilterQueryProps} from 'shared/util/encode-filters';
import useEditSavedFields from 'modules/users/hooks/use-edit-saved-fields';
import useUserProjection from 'modules/users/atoms/use-user-projection';
import {OrderByProps} from 'shared/migration-components/api-table/state';

export const filterAtom = persistentAtom<KeyValueProps<FilterQueryProps>>(
  'filters',
  {},
);

export const orderingAtom = persistentAtom<KeyValueProps<OrderByProps>>(
  'ordering',
  {},
);
interface ProjectionProps {
  defaultFilters?: FilterQueryProps;
  orderBy?: OrderByProps;
  overrideDefaultValues?: boolean;
}

export const useProjection = <T = any>(
  key: string,
  values: NestedKeys<T>[],
  options?: ProjectionProps,
) => {
  const {defaultFilters, orderBy, overrideDefaultValues} = options ?? {};
  const [filters, setFilters] = useAtom(filterAtom);
  const [ordering, setOrdering] = useAtom(orderingAtom);
  const editSavedFieldsMutation = useEditSavedFields();
  const {userProjection, setUserProjection} = useUserProjection();

  useEffect(() => {
    if (orderBy)
      setOrdering((prev: KeyValueProps<OrderByProps>) => ({
        ...prev,
        [key]: orderBy,
      }));
  }, []);

  const savedFields = useMemo(() => {
    if (userProjection && key in userProjection) {
      return userProjection[key];
    }
    return [];
  }, [userProjection, key]);

  const [projection, setProjection] = useState<any[]>(
    savedFields.length > 0 ? savedFields : values,
  );

  useEffect(() => {
    if (
      (overrideDefaultValues ||
        !filters[key] ||
        Object.keys(filters[key]).length === 0) &&
      defaultFilters
    )
      setFilters({...filters, [key]: defaultFilters});
  }, []);

  const onChange = (data: (NestedKeys<T> | string)[]) => {
    setProjection([...data]);
    setUserProjection({...(userProjection ?? {}), [key]: data});

    if (userProjection && projection.length > 0) {
      editSavedFieldsMutation.mutate({...userProjection, [key]: data});
    }
  };

  return {
    values: projection,
    onChange,
    filter: {
      filters: filters[key] ?? {},
      onChange: (data: FilterQueryProps) =>
        setFilters({...filters, [key]: data}),
    },
    orderBy: {
      ...((ordering[key] ?? {}) as OrderByProps),
      onChange: (data: OrderByProps) => setOrdering({...ordering, [key]: data}),
    },
  };
};

export default useProjection;
